import {
    Component,
    ComponentRef,
    Input,
    OnInit,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { debounceTime, tap } from 'rxjs/operators';
import { ShoppingCartLine } from '../../../generic/classes/shoppingcartline';
import { DynamicModalDirective } from '../../../generic/component/modal/dynamic.modal.directive';
import { ModalWrapperComponent } from '../../../generic/component/modal/modal.wrapper.component';
import { AddCommentComponent } from '../../../generic/component/my/order/pending/add.comment.component';
import { ApplicationService } from '../../../generic/service/application.service';
import { ProductService } from '../../../generic/service/product.service';
import { ShoppingCartService } from '../../../generic/service/shoppingcart.service';
import { UserService } from '../../../generic/service/user.service';
import { NumberFormatPipe } from '../../../generic/pipes/number.format.pipe';
import { CartLineRemoveComponent } from '../../../generic/component/cart/cart.line.remove.component';
import { RenderProductImageComponent } from '../../../generic/component/products/render.product.image';

@Component({
    selector: '[shopping-cart-row]',
    templateUrl: 'shopping.cart.row.html',
    standalone: true,
    imports: [
        RenderProductImageComponent,
        RouterLink,
        FormsModule,
        ReactiveFormsModule,
        CartLineRemoveComponent,
        DynamicModalDirective,
        NumberFormatPipe,
    ],
})
export class ShoppingCartRowComponent implements OnInit {
    @Input() shoppingCartLine: ShoppingCartLine;
    @Input() closingTime: number;
    @Input() showBarcode: boolean;
    @Input() allowComment: boolean;
    @Input() isEditable: boolean = false;
    hasComment: boolean = false;
    commentText: string;
    _componentRef: ComponentRef<any>;

    @ViewChild(DynamicModalDirective) modalWrapper: DynamicModalDirective;

    quantityValue: number;
    minValue = 0;
    image: string;
    valueForm: UntypedFormGroup;
    expiryDate: Date;
    removeButtonDisabled: boolean = false;

    constructor(
        private router: Router,
        private viewContainerRef: ViewContainerRef,
        private shoppingCartService: ShoppingCartService,
        private productService: ProductService,
        private userService: UserService,
        private formBuilder: UntypedFormBuilder,
        public applicationService: ApplicationService,
    ) {}

    ngOnInit() {
        this.quantityValue = this.getQuantity();

        this.image = this.productService.getImage(this.shoppingCartLine.image);

        this.valueForm = this.formBuilder.group({
            quantity: [this.quantityValue, Validators.required],
        });

        const quantityControl = this.valueForm.controls.quantity;

        this.minValue = 1;

        // See if Cart line has a comment
        if (this.shoppingCartLine.comment) {
            this.hasComment = true;
            this.commentText = this.shoppingCartLine.comment;
        }

        // Listen for value changes, but give the user a chance to change the value multiple times
        // within the debounce time in ms
        quantityControl.valueChanges
            .pipe(
                tap(value => {
                    if (value !== this.quantityValue) {
                        // Disable the remove button if the value has changed.
                        this.removeButtonDisabled = true;
                    }
                }),
                debounceTime(1000),
            )
            .subscribe((value: string) => {
                this.changeQuantity(value);
            });

        if (this.shoppingCartLine.shoppingCartMode) {
            this.isEditable = !this.shoppingCartLine.confirmed;
        } else {
            // Only relevant for fast checkout mode

            this.isEditable =
                this.shoppingCartService.isShoppingCartLineEditable(
                    this.shoppingCartLine,
                );

            if (this.isEditable) {
                // Needed for the cart 'line remove' child component
                this.expiryDate =
                    this.shoppingCartService.getShoppingCartLineExpiryDate(
                        this.shoppingCartLine,
                    );

                const interval = setInterval(() => {
                    this.isEditable =
                        this.shoppingCartService.isShoppingCartLineEditable(
                            this.shoppingCartLine,
                        );

                    // Disable setinterval if the line is disabled
                    if (!this.isEditable) {
                        clearInterval(interval);
                    }
                }, 1000);
            }
        }
    }

    getUserCartWaitTimeInSeconds(): number {
        return this.userService.getUserCartWaitTimeInSeconds();
    }

    /**
     * Calculates the total price for the given shopping cart line
     *
     * @returns {number}
     */
    getPriceForLine() {
        return this.shoppingCartLine.unitPriceExcResale;
    }

    /**
     * Only call the service if the value really changed
     *
     * @param value
     */
    changeQuantity(value: any) {
        if (value !== this.quantityValue) {
            const numberOfItemsToOrder = value * this.shoppingCartLine.qtyByBox;
            this.quantityValue = value;

            this.shoppingCartLine.quantity = numberOfItemsToOrder;
            this.shoppingCartService.changeQuantity(this.shoppingCartLine);

            // Enable the remove button.
            this.removeButtonDisabled = false;
        }
    }

    removeProduct() {
        if (this.isEditable) {
            this.shoppingCartService.removeProduct(this.shoppingCartLine);
        }
    }

    addCommentView() {
        this.showModal({
            description: this.shoppingCartLine.description,
            comment: this.commentText,
            webuuid: this.shoppingCartLine.webUuid,
            isEditable: this.isEditable,
        });
    }

    /**
     * Make sure the quantity is rounded to a number
     * @returns {number}
     */
    getQuantity(): number {
        const quantity =
            this.shoppingCartLine.quantity / this.shoppingCartLine.qtyByBox;

        return Math.round(quantity);
    }

    private showModal(data: any) {
        const modalWrapper = new ModalWrapperComponent(
            AddCommentComponent,
            data,
        );
        const viewContainerRef = this.modalWrapper.viewContainerRef;
        viewContainerRef.clear();

        this._componentRef = viewContainerRef.createComponent(
            modalWrapper.component,
        );
        this._componentRef.instance._componentRef = this._componentRef;
        (<ModalWrapperComponent>this._componentRef.instance).data = data;

        // Listen to changes from the comment dialog
        this._componentRef.instance.addedComment.subscribe(hasComment => {
            this.hasComment = hasComment;
        });

        this._componentRef.instance.commentValue.subscribe(comment => {
            this.commentText = comment;
        });
    }
}
